/**
 * MentionPopover — Auswahlliste für @-Erwähnungen über einem Chat-Eingabefeld.
 *
 * Wird von ChatPage (Team-Chat) und KommunikationTab (Kunden-Chat) benutzt.
 * Die Erkennung des Suchbegriffs steckt in lib/mentions.ts; hier ist nur die
 * Darstellung und die Tastatursteuerung (↑/↓/Enter/Tab/Escape).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import { mentionName } from '../../lib/mentions';
import type { V1Admin } from '../../lib/v1';

interface MentionPopoverProps {
  dark: boolean;
  candidates: V1Admin[];
  activeIndex: number;
  onPick: (admin: V1Admin) => void;
}

export function MentionPopover({ dark, candidates, activeIndex, onPick }: MentionPopoverProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  if (candidates.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label={tr('chat.mention.aria_label')}
      style={{
        position: 'absolute', bottom: '100%', left: 0, marginBottom: 6,
        minWidth: 220, maxHeight: 220, overflowY: 'auto', zIndex: 40,
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
        boxShadow: '0 8px 24px rgba(16,24,40,0.14)', padding: 4,
      }}
    >
      {candidates.map((a, i) => (
        <button
          key={a.id}
          type="button"
          role="option"
          aria-selected={i === activeIndex}
          // onMouseDown statt onClick: ein Klick würde sonst erst den Fokus
          // aus dem Eingabefeld nehmen und die Cursorposition verlieren.
          onMouseDown={e => { e.preventDefault(); onPick(a); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
            width: '100%', textAlign: 'left', cursor: 'pointer',
            border: 'none', borderRadius: 7, padding: '6px 9px',
            background: i === activeIndex ? (dark ? '#1e2735' : '#f2f4f7') : 'transparent',
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          <span style={{ fontSize: 13, color: t.heading, fontWeight: 500 }}>{mentionName(a)}</span>
          {a.email && <span style={{ fontSize: 11, color: t.muted }}>{a.email}</span>}
        </button>
      ))}
    </div>
  );
}
